import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { STLLoader, OBJLoader, OrbitControls } from "three-stdlib";
import { Box, Loader2, AlertTriangle, FileQuestion, Move3d, RotateCcw } from "lucide-react";
import { geometryStats } from "../lib/modelAnalysis";
import { pub } from "@/lib/pubThemeClasses";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Pure three.js (no R3F JSX) — bypasses visual-edits babel plugin which
 * injects x-line-number on every JSX element and breaks R3F's applyProps.
 *
 * Props:
 *  - file: File | null
 *  - onAnalysis: (meta) => void
 */
export default function ModelViewer({ file, onAnalysis }) {
  const { t } = useLanguage();
  const [status, setStatus] = useState("idle"); // idle | loading | ready | step | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [fileExt, setFileExt] = useState(null);
  const [resetTick, setResetTick] = useState(0);

  const mountRef = useRef(null);
  const sceneObjRef = useRef({
    renderer: null,
    scene: null,
    camera: null,
    controls: null,
    currentModel: null,
    raf: null,
    initialCamPos: null,
    initialTarget: null,
  });

  // Initialize renderer once, when we enter 'ready' state
  useEffect(() => {
    if (status !== "ready" || !mountRef.current) return;

    const mount = mountRef.current;
    const width = mount.clientWidth || 600;
    const height = mount.clientHeight || 420;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    renderer.setSize(width, height);
    renderer.setClearColor(0x050505, 1);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050505);

    const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 5000);
    camera.position.set(80, 80, 80);

    const ambient = new THREE.AmbientLight(0xffffff, 0.55);
    const dir1 = new THREE.DirectionalLight(0xffffff, 1.1);
    dir1.position.set(50, 80, 40);
    const dir2 = new THREE.DirectionalLight(0xff4f00, 0.35);
    dir2.position.set(-40, -20, -40);
    const hemi = new THREE.HemisphereLight(0xffffff, 0x1a1a1f, 0.35);
    scene.add(ambient, dir1, dir2, hemi);

    const grid = new THREE.GridHelper(1000, 100, 0x1f1f24, 0x14141a);
    scene.add(grid);

    const axes = new THREE.AxesHelper(40);
    axes.material.depthTest = false;
    axes.renderOrder = 999;
    scene.add(axes);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 2;
    controls.maxDistance = 5000;

    sceneObjRef.current.renderer = renderer;
    sceneObjRef.current.scene = scene;
    sceneObjRef.current.camera = camera;
    sceneObjRef.current.controls = controls;

    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      sceneObjRef.current.raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      if (!mount) return;
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);
    const sceneRef = sceneObjRef.current;

    return () => {
      cancelAnimationFrame(sceneRef.raf);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
      scene.traverse((c) => {
        if (c.geometry) c.geometry.dispose?.();
        if (c.material) {
          if (Array.isArray(c.material)) c.material.forEach((m) => m.dispose?.());
          else c.material.dispose?.();
        }
      });
      sceneRef.renderer = null;
      sceneRef.scene = null;
      sceneRef.camera = null;
      sceneRef.controls = null;
      sceneRef.currentModel = null;
    };
  }, [status]);

  // Handle file changes
  useEffect(() => {
    if (!file) {
      setStatus("idle");
      setFileExt(null);
      setErrorMsg(null);
      // clear previous model from scene if any
      clearModel();
      return;
    }

    const ext = (file.name.split(".").pop() || "").toLowerCase();

    if (ext === "step" || ext === "stp") {
      clearModel();
      setStatus("step");
      setFileExt("STEP");
      onAnalysis?.({
        ext: "STEP",
        size: null,
        triangles: null,
        volumeMm3: null,
        fileName: file.name,
        fileSize: file.size,
      });
      return;
    }

    if (ext === "3mf") {
      clearModel();
      setStatus("3mf");
      setFileExt("3MF");
      onAnalysis?.({
        ext: "3MF",
        size: null,
        triangles: null,
        volumeMm3: null,
        fileName: file.name,
        fileSize: file.size,
      });
      return;
    }

    if (ext !== "stl" && ext !== "obj") {
      setStatus("error");
      setErrorMsg("viewer.error.unsupportedFormat");
      return;
    }

    setStatus("loading");
    setErrorMsg(null);
    setFileExt(ext.toUpperCase());

    const reader = new FileReader();
    reader.onerror = () => {
      setStatus("error");
      setErrorMsg("viewer.error.readFailed");
    };
    reader.onload = (e) => {
      try {
        if (ext === "stl") {
          const geom = new STLLoader().parse(e.target.result);
          geom.computeBoundingBox();
          geom.computeVertexNormals();
          const bbox = geom.boundingBox;
          const size = new THREE.Vector3();
          bbox.getSize(size);
          const center = new THREE.Vector3();
          bbox.getCenter(center);
          geom.translate(-center.x, -center.y, -center.z);

          const { triangles, volumeMm3 } = geometryStats(geom);
          const material = new THREE.MeshStandardMaterial({
            color: 0xff4f00,
            metalness: 0.18,
            roughness: 0.5,
          });
          const mesh = new THREE.Mesh(geom, material);
          setStatus("ready");
          // model is added by a separate effect (needs scene to exist)
          pendingModel.current = { obj: mesh, size };
          onAnalysis?.({
            ext: "STL",
            size,
            triangles,
            volumeMm3,
            fileName: file.name,
            fileSize: file.size,
          });
        } else {
          const text = e.target.result;
          const grp = new OBJLoader().parse(text);
          const bbox = new THREE.Box3().setFromObject(grp);
          const size = new THREE.Vector3();
          bbox.getSize(size);
          const center = new THREE.Vector3();
          bbox.getCenter(center);
          grp.position.sub(center);

          let triangles = 0;
          grp.traverse((ch) => {
            if (ch.isMesh && ch.geometry) {
              const g = ch.geometry;
              triangles += g.index ? g.index.count / 3 : g.attributes.position.count / 3;
              if (!ch.material || Array.isArray(ch.material)) {
                ch.material = new THREE.MeshStandardMaterial({
                  color: 0xff4f00,
                  metalness: 0.15,
                  roughness: 0.55,
                });
              }
            }
          });

          setStatus("ready");
          pendingModel.current = { obj: grp, size };
          onAnalysis?.({
            ext: "OBJ",
            size,
            triangles,
            volumeMm3: null,
            fileName: file.name,
            fileSize: file.size,
          });
        }
      } catch (err) {
        console.error("Model parse error:", err);
        setStatus("error");
        setErrorMsg("viewer.error.parseFailed");
      }
    };

    if (ext === "stl") reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // When a model is pending AND scene is ready (status==='ready'), add it
  const pendingModel = useRef(null);
  useEffect(() => {
    if (status !== "ready") return;
    // wait for scene to be set by first effect
    const tryAdd = () => {
      if (!sceneObjRef.current.scene) {
        requestAnimationFrame(tryAdd);
        return;
      }
      if (pendingModel.current) {
        clearModel();
        const { obj, size } = pendingModel.current;
        sceneObjRef.current.scene.add(obj);
        sceneObjRef.current.currentModel = obj;
        fitCamera(size);
        pendingModel.current = null;
      } else if (resetTick > 0) {
        // just refit for existing model on reset
        if (sceneObjRef.current.currentModel) {
          const bbox = new THREE.Box3().setFromObject(sceneObjRef.current.currentModel);
          const s = new THREE.Vector3();
          bbox.getSize(s);
          fitCamera(s);
        }
      }
    };
    tryAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, resetTick]);

  function clearModel() {
    const { scene, currentModel } = sceneObjRef.current;
    if (scene && currentModel) {
      scene.remove(currentModel);
      currentModel.traverse?.((c) => {
        if (c.geometry) c.geometry.dispose?.();
        if (c.material) {
          if (Array.isArray(c.material)) c.material.forEach((m) => m.dispose?.());
          else c.material.dispose?.();
        }
      });
      if (currentModel.geometry) currentModel.geometry.dispose?.();
      if (currentModel.material) currentModel.material.dispose?.();
    }
    sceneObjRef.current.currentModel = null;
  }

  function fitCamera(size) {
    const { camera, controls } = sceneObjRef.current;
    if (!camera || !size) return;
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const fov = (camera.fov * Math.PI) / 180;
    const distance = (maxDim / (2 * Math.tan(fov / 2))) * 1.6;
    camera.position.set(distance, distance * 0.85, distance);
    camera.near = Math.max(0.1, distance / 200);
    camera.far = distance * 100;
    camera.updateProjectionMatrix();
    camera.lookAt(0, 0, 0);
    if (controls) {
      controls.target.set(0, 0, 0);
      controls.update();
    }
  }

  return (
    <div data-testid="model-viewer" className={`border bg-[#0C0C0F] overflow-hidden relative ${pub.borderSubtle}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b bg-[#050505] ${pub.panelInnerDivide}`}>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          <Move3d className="w-3.5 h-3.5 text-[#FF4F00]" />
          <span>
            {t("viewer.badge.preview")} · {status === "ready" ? fileExt : status === "step" ? "STEP" : status === "3mf" ? "3MF" : t("viewer.badge.live")}
          </span>
        </div>
        {status === "ready" && (
          <button
            type="button"
            onClick={() => setResetTick((k) => k + 1)}
            data-testid="viewer-reset-button"
            className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-zinc-100`}
          >
            <RotateCcw className="w-3 h-3" /> {t("viewer.reset")}
          </button>
        )}
      </div>

      {/* Canvas / State area */}
      <div
        className="relative h-[320px] md:h-[420px] bg-[#050505]"
        data-testid="viewer-canvas-wrapper"
      >
        <div
          ref={mountRef}
          className="absolute inset-0"
          style={{ display: status === "ready" ? "block" : "none" }}
        />
        {status === "idle" && <EmptyState />}
        {status === "loading" && <LoadingState />}
        {status === "error" && <ErrorState message={errorMsg} />}
        {status === "step" && <StepState />}
        {status === "3mf" && <ThreeMfState />}
      </div>

      {/* Controls hint */}
      {status === "ready" && (
        <div className={`px-4 py-2.5 border-t bg-[#050505] flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 ${pub.panelInnerDivide}`}>
          <span>{t("viewer.hint.rotate")}</span>
          <span>{t("viewer.hint.pan")}</span>
          <span>{t("viewer.hint.zoom")}</span>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useLanguage();
  return (
    <div className="absolute inset-0 grid place-items-center p-8 text-center">
      <div>
        <div className={`w-14 h-14 border mx-auto grid place-items-center mb-5 opacity-90 ${pub.panelInnerDivide}`}>
          <Box className="w-6 h-6 text-zinc-500" strokeWidth={1.3} />
        </div>
        <div className="font-display text-lg md:text-xl font-bold uppercase tracking-tight text-zinc-200">
          {t("viewer.empty.title1")}
          <br /> {t("viewer.empty.title2")}
        </div>
        <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">{t("viewer.empty.formats")}</div>
      </div>
    </div>
  );
}

function LoadingState() {
  const { t } = useLanguage();
  return (
    <div className="absolute inset-0 grid place-items-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-[#FF4F00] animate-spin mx-auto mb-4" />
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">{t("viewer.loading")}</div>
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  const { t } = useLanguage();
  const body =
    message && String(message).startsWith("viewer.error.") ? t(message) : message || t("viewer.error.fallback");
  return (
    <div className="absolute inset-0 grid place-items-center p-6">
      <div className="text-center">
        <div className="w-12 h-12 border border-red-500/40 bg-red-500/5 mx-auto grid place-items-center mb-4">
          <AlertTriangle className="w-5 h-5 text-red-400" strokeWidth={1.5} />
        </div>
        <div className="font-display text-base font-bold uppercase tracking-tight text-red-300 mb-1">{t("viewer.error.title")}</div>
        <div className="text-xs text-zinc-500 max-w-xs">{body}</div>
      </div>
    </div>
  );
}

function StepState() {
  const { t } = useLanguage();
  return (
    <div className="absolute inset-0 grid place-items-center p-6">
      <div className="text-center max-w-sm">
        <div className="w-12 h-12 border border-[#00E5FF]/40 bg-[#00E5FF]/5 mx-auto grid place-items-center mb-5">
          <FileQuestion className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
        </div>
        <div className="font-display text-base md:text-lg font-bold uppercase tracking-tight mb-3">{t("viewer.step.title")}</div>
        <div className="text-xs text-zinc-400 leading-relaxed">{t("viewer.step.body")}</div>
      </div>
    </div>
  );
}

function ThreeMfState() {
  const { t } = useLanguage();
  return (
    <div className="absolute inset-0 grid place-items-center p-6">
      <div className="text-center max-w-sm">
        <div className="w-12 h-12 border border-[#00FF9D]/30 bg-[#00FF9D]/5 mx-auto grid place-items-center mb-5">
          <FileQuestion className="w-5 h-5 text-[#00FF9D]" strokeWidth={1.5} />
        </div>
        <div className="font-display text-base md:text-lg font-bold uppercase tracking-tight mb-3">{t("viewer.3mf.title")}</div>
        <div className="text-xs text-zinc-400 leading-relaxed">{t("viewer.3mf.body")}</div>
      </div>
    </div>
  );
}
